/**
 * One choice out of two or three, drawn as the app's segmented control.
 *
 * ── Native radios, not a hand-rolled roving tab stop ────────────────────────────────────────
 * `ScopeSwitch` and `RosterToolbar` already state the rule this follows: a radio group is one of
 * the very few controls the platform gets right. A `<fieldset>` with a `<legend>` and real
 * `<input type="radio">`s inside their labels gives the group its name, the set its single tab
 * stop, and the arrows their meaning — for free, and identically in every browser. A roving
 * `tabIndex` over `<button>`s would be a re-implementation of all three that nobody tests.
 *
 * **NOT `role="tablist"`**, for `ScopeSwitch`'s reason: a tablist promises that an arrow both moves
 * AND activates, which is right for a tab strip and wrong here — arrowing across the theme options
 * would repaint the whole app three times on the way past.
 *
 * ── It exists because there are three of them ───────────────────────────────────────────────
 * Theme, interface language, exercise-content language. One would be a pass-through; three of the
 * same wiring in one file is the shape this extraction is for, and the caller ends up knowing less:
 * it hands over a legend, some options and a handler, and knows nothing about names, ids or rings.
 *
 * The SKIN is `shared/ui/segmented.module.css`, promoted out of `/raporty` on this screen. The
 * component itself left `pages/account/ui` on 25 September 2026, on its first consumer outside that
 * page: the exercise filter panels' `Język` tab (`entities/exercise-library`).
 *
 * ── Two cuts of one control, because the canon draws two ────────────────────────────────────
 * `raised` is the app's groove with a lifted card in it — the three rows of the Aplikacja tab.
 * `ink` is the billing-period switch inside a plan card: full width, a hairline groove and an INK
 * fill on the chosen option, because a `--bg-card` pill standing on a `--bg-card` card is
 * invisible. One prop rather than a second component: the wiring, the names and the ring are the
 * same control, and only the skin differs.
 */

import { useId } from 'react';

import hiddenControl from '~/shared/ui/visuallyHiddenControl.module.css';
import seg from '~/shared/ui/segmented.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

export interface SegmentedOption<Value extends string> {
  readonly value: Value;
  readonly label: string;
  /** An optional mark before the word — the three theme glyphs. Decorative; the word carries it. */
  readonly Icon?: (props: { className?: string }) => React.JSX.Element;
  /**
   * A short mark AFTER the word, inside the same option — the yearly saving, „−48%".
   *
   * It is part of the option's accessible name on purpose: the percentage is the reason a trainer
   * presses this option, so a listener has to hear it where a reader sees it.
   */
  readonly badge?: string;
}

interface SegmentedChoiceProps<Value extends string> {
  /** The group's accessible name. Visually hidden: the row's own title already states it. */
  readonly legend: string;
  readonly options: readonly SegmentedOption<Value>[];
  readonly value: Value;
  readonly onChange: (value: Value) => void;
  /** `raised` is the app's groove; `ink` is the card's full-width switch. See the header. */
  readonly tone?: 'raised' | 'ink';
  /** `compact` is the plan overview's denser cut — its switches sit four to a screen. */
  readonly size?: 'regular' | 'compact';
}

export function SegmentedChoice<Value extends string>({
  legend,
  options,
  value,
  onChange,
  tone = 'raised',
  size = 'regular',
}: SegmentedChoiceProps<Value>) {
  // One name per rendered group, so two groups on the same tab are two independent choices.
  const name = useId();

  return (
    <fieldset
      className={`${String(seg.seg)} ${tone === 'ink' ? String(seg.segInk) : ''} ${
        size === 'compact' ? String(seg.segCompact) : ''
      }`}
    >
      {/* Hidden rather than `display: none`, which would take the group's name out of the
        * accessibility tree — the whole reason a fieldset is used here. */}
      <legend className={hidden.srOnly}>{legend}</legend>
      {options.map((option) => (
        <label
          key={option.value}
          className={`${String(seg.segOption)} ${
            option.value !== value
              ? ''
              : tone === 'ink'
                ? String(seg.segOptionInkOn)
                : String(seg.segOptionOn)
          }`}
        >
          <input
            // `visuallyHiddenControl`, not `visuallyHidden`: a FOCUSABLE control pinned to the
            // viewport's corner has nowhere to be scrolled to, and it sits under whatever is drawn
            // there. `.segOption` is the positioned ancestor it resolves against.
            className={hiddenControl.control}
            type="radio"
            name={name}
            value={option.value}
            checked={option.value === value}
            onChange={() => {
              onChange(option.value);
            }}
          />
          {option.Icon !== undefined && <option.Icon className={seg.segIcon} />}
          {option.label}
          {option.badge !== undefined && <span className={seg.segBadge}>{option.badge}</span>}
        </label>
      ))}
    </fieldset>
  );
}
